import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Container,
  TextField,
  Button,
  Paper,
  List,
  ListItem,
  ListItemText,
  IconButton,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Snackbar,
  Alert,
  CircularProgress
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import firebaseService from '../firebase/firebaseService';
import { auth } from '../firebase/config';
import { useNavigate } from 'react-router-dom';
import { collection, onSnapshot, query, orderBy, doc, deleteDoc } from 'firebase/firestore';
import { db } from '../firebase/config';

const AdminPage = () => {
  const [allowedEmails, setAllowedEmails] = useState([]);
  const [newEmail, setNewEmail] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [emailToDelete, setEmailToDelete] = useState(null);
  const navigate = useNavigate();

  // Check if user is admin
  useEffect(() => {
    const checkAdminAccess = async () => {
      try {
        const user = auth.currentUser;
        if (!user) {
          // Not logged in, redirect to login
          navigate('/');
          return;
        }

        const isAdmin = await firebaseService.isUserAdmin(user.uid);
        if (!isAdmin) {
          // Not an admin, redirect to home
          navigate('/');
          setError('You do not have permission to access this page');
        }
      } catch (error) {
        console.error('Error checking admin status:', error);
        navigate('/');
      }
    };

    checkAdminAccess();
  }, [navigate]);

  // Load allowed emails
  useEffect(() => {
    const allowedEmailsRef = collection(db, 'allowedEmails');
    const q = query(allowedEmailsRef, orderBy('addedAt', 'desc'));

    const unsubscribe = onSnapshot(q, (snapshot) => {
      const emails = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setAllowedEmails(emails);
      setLoading(false);
    }, (error) => {
      console.error('Error loading allowed emails:', error);
      setError('Error loading allowed emails');
      setLoading(false);
    });

    return () => unsubscribe();
  }, []);

  const handleAddEmail = async (e) => {
    e.preventDefault();
    
    if (!newEmail) {
      setError('Please enter an email address');
      return;
    }
    
    try {
      setLoading(true);
      await firebaseService.addAllowedEmail(newEmail);
      setNewEmail('');
      setSuccess(`${newEmail} added to allowed list`);
    } catch (error) {
      console.error('Error adding email:', error);
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteEmail = async () => {
    if (!emailToDelete) return;
    
    try {
      setLoading(true);
      await deleteDoc(doc(db, 'allowedEmails', emailToDelete));
      setSuccess(`Email removed from allowed list`);
      setDeleteDialogOpen(false);
      setEmailToDelete(null);
    } catch (error) {
      console.error('Error deleting email:', error);
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  const openDeleteDialog = (email) => {
    setEmailToDelete(email);
    setDeleteDialogOpen(true);
  };

  return (
    <Container maxWidth="md" sx={{ py: 4 }}>
      <Typography variant="h4" component="h1" gutterBottom>
        Admin Panel - Manage Allowed Emails
      </Typography>
      
      <Paper sx={{ p: 3, mb: 4, bgcolor: 'rgba(25, 118, 210, 0.08)' }}>
        <Typography variant="subtitle1" gutterBottom>
          <strong>Admin Access Notice:</strong> Only authorized administrators can manage allowed emails. 
          Any changes made here will control who can access the application.
        </Typography>
      </Paper>
      
      <Paper sx={{ p: 3, mb: 4 }}>
        <Typography variant="h6" gutterBottom>
          Add New Allowed Email
        </Typography>
        <Box component="form" onSubmit={handleAddEmail} sx={{ display: 'flex', gap: 2 }}>
          <TextField
            fullWidth
            label="Email Address"
            variant="outlined"
            value={newEmail}
            onChange={(e) => setNewEmail(e.target.value)}
            placeholder="Enter email to allow"
          />
          <Button 
            type="submit" 
            variant="contained" 
            color="primary"
            startIcon={<AddIcon />}
            disabled={loading}
          >
            Add
          </Button>
        </Box>
      </Paper>
      
      <Paper sx={{ p: 3 }}>
        <Typography variant="h6" gutterBottom>
          Allowed Emails
        </Typography>
        
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
            <CircularProgress />
          </Box>
        ) : allowedEmails.length === 0 ? (
          <Typography color="text.secondary" sx={{ p: 2 }}>
            No emails in the allowlist yet
          </Typography>
        ) : (
          <List>
            {allowedEmails.map((item) => (
              <React.Fragment key={item.id}>
                <ListItem
                  secondaryAction={
                    <IconButton edge="end" onClick={() => openDeleteDialog(item.id)}>
                      <DeleteIcon />
                    </IconButton>
                  }
                >
                  <ListItemText
                    primary={item.id}
                    secondary={item.addedBy ? `Added by: ${item.addedBy}` : ''}
                  />
                </ListItem>
                <Divider />
              </React.Fragment>
            ))}
          </List>
        )}
      </Paper>
      
      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>Confirm Removal</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to remove this email from the allowed list?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleDeleteEmail} color="error">Remove</Button>
        </DialogActions>
      </Dialog>
      
      {/* Error Snackbar */}
      <Snackbar 
        open={!!error} 
        autoHideDuration={6000} 
        onClose={() => setError(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={() => setError(null)} severity="error" sx={{ width: '100%' }}>
          {error}
        </Alert>
      </Snackbar>
      
      {/* Success Snackbar */}
      <Snackbar 
        open={!!success} 
        autoHideDuration={6000} 
        onClose={() => setSuccess(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={() => setSuccess(null)} severity="success" sx={{ width: '100%' }}>
          {success}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default AdminPage; 